/**
 * Key dietary-tag selection for inline menu-item rows.
 *
 * Ordering surfaces receive `dietaryTags` as reviewed display names (e.g.
 * "Vegetarian", "Gluten-Free") off the contains rollup. Compact inline rows
 * (the school order grid) surface only the KEY claims school staff check at a
 * glance — everything else stays in the click-in detail modal.
 *
 * Matching is by canonical contains-tag SLUG, not raw display label: names
 * normalize through the same slug normalization `@ordo/ui/tag-chip` uses for
 * chip colors ("Gluten Free" / "Gluten-Free" / "gluten_free" → "gluten-free"),
 * then compare against the canonical slugs defined in `@ordo/schema`'s
 * contains policy (`packages/schema/src/managers/contains-policy.ts` —
 * not importable client-side, so the slug constants are restated here).
 */

/**
 * Canonical slugs of the key claims, in inline display order. Must stay a
 * subset of `CANONICAL_CONTAINS_TAGS` dietary slugs in
 * `@ordo/schema` contains-policy.
 */
export const KEY_DIETARY_TAG_SLUGS = ['vegetarian', 'halal', 'vegan', 'gluten-free', 'dairy-free', 'egg-free'] as const

export type KeyDietaryTagSlug = (typeof KEY_DIETARY_TAG_SLUGS)[number]

/**
 * Normalize a dietary tag display name or slug to its canonical slug key —
 * the same normalization `@ordo/ui/tag-chip` applies when resolving chip
 * colors, so name→slug agreement is guaranteed with the chip palette.
 */
export function dietaryTagSlug(labelOrSlug: string): string {
  return labelOrSlug
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
}

const KEY_SLUG_ORDER = new Map<string, number>(KEY_DIETARY_TAG_SLUGS.map((slug, index) => [slug, index]))

/**
 * Filter a payload's dietary tag names down to the KEY claims for inline
 * display, deduped (first spelling wins) and ordered by
 * {@link KEY_DIETARY_TAG_SLUGS} regardless of payload order. Returns the
 * original display labels; `[]` for absent/empty input (callers render no
 * container in that case).
 */
export function keyDietaryTags(tags: readonly string[] | null | undefined): string[] {
  if (!tags || tags.length === 0) {
    return []
  }
  const bySlug = new Map<string, string>()
  for (const tag of tags) {
    const slug = dietaryTagSlug(tag)
    if (KEY_SLUG_ORDER.has(slug) && !bySlug.has(slug)) {
      bySlug.set(slug, tag)
    }
  }
  return [...bySlug.entries()]
    .sort(([a], [b]) => (KEY_SLUG_ORDER.get(a) ?? 0) - (KEY_SLUG_ORDER.get(b) ?? 0))
    .map(([, label]) => label)
}
